import { buildAppsFlyerEnrollmentOneLinkUrl } from 'common/buildAppsFlyerEnrollmentOneLinkUrl';
import { WEB_ENROLLMENT_ORIGIN } from 'common/env';
import {
    getWebP2PInviteUtmParams,
    WebP2PInviteShareMethod,
    WebP2PInviteSource,
} from 'common/p2pInviteUtm';
import { toQueryString } from 'common/utils/queryString';

const P2P_WEB_ATTRIBUTION = { pid: 'web_invite', c: 'P2P' } as const;

/** Builds a web P2P enrollment link whose UTM medium reflects the share action. */
export function getP2PEnrollmentUrl(
    enrollmentCode: string,
    method: WebP2PInviteShareMethod,
    source: WebP2PInviteSource,
    isAppsFlyerP2PEnabled: boolean
) {
    const utmParams = getWebP2PInviteUtmParams(source, method);
    if (isAppsFlyerP2PEnabled) {
        return buildAppsFlyerEnrollmentOneLinkUrl(enrollmentCode, P2P_WEB_ATTRIBUTION, {
            webEnrollmentQueryParams: utmParams,
            appDeepLinkQueryParams: utmParams,
        });
    }

    return `${WEB_ENROLLMENT_ORIGIN}/enroll/${enrollmentCode}?${toQueryString(utmParams)}`;
}

/** P2P-only bridge path for Email and Text actions before redirecting to AppsFlyer. */
export function getP2PInviteRedirectPath(
    enrollmentCode: string,
    method: Extract<WebP2PInviteShareMethod, 'email' | 'text'>,
    source: WebP2PInviteSource
) {
    return `/invite/web/${method}/${enrollmentCode}?${toQueryString({
        p2p: true,
        ...getWebP2PInviteUtmParams(source, method),
    })}`;
}

export function getEnrollmentUrl(enrollmentCode: string) {
    const baseUrl = `${WEB_ENROLLMENT_ORIGIN}/enroll/${enrollmentCode}`;
    const utmParams =
        'utm_source=coordinator_invite&utm_medium=invite_link&utm_campaign=dashboard_invite_tools';
    return `${baseUrl}?${utmParams}`;
}

export function getEnrollmentUrlWithAppsFlyer(
    enrollmentCode: string,
    isAppsFlyerP2PEnabled: boolean
) {
    if (isAppsFlyerP2PEnabled) {
        return buildAppsFlyerEnrollmentOneLinkUrl(enrollmentCode, P2P_WEB_ATTRIBUTION);
    }

    const baseUrl = `${WEB_ENROLLMENT_ORIGIN}/enroll/${enrollmentCode}`;
    const utmParams = 'utm_source=p2p_invite&utm_medium=invite_link&utm_campaign=web_p2p_invite';
    return `${baseUrl}?${utmParams}`;
}

/** Alias of getEnrollmentUrlWithAppsFlyer (call sites historically named “email”). */
export function getEnrollmentUrlWithAppsFlyerEmail(
    enrollmentCode: string,
    isAppsFlyerP2PEnabled: boolean
) {
    return getEnrollmentUrlWithAppsFlyer(enrollmentCode, isAppsFlyerP2PEnabled);
}

export function getEnrollmentUrlQR(enrollmentCode: string) {
    const baseUrl = `${WEB_ENROLLMENT_ORIGIN}/enroll/${enrollmentCode}`;
    const utmParams =
        'utm_source=coordinator_invite&utm_medium=qr_code&utm_campaign=dashboard_invite_tools';
    return `${baseUrl}?${utmParams}`;
}

/** Invite link surfaced on the coordinator Invite Hub (Participants page). */
export function getInviteHubEnrollmentUrl(enrollmentCode: string) {
    const baseUrl = `${WEB_ENROLLMENT_ORIGIN}/enroll/${enrollmentCode}`;
    const utmParams =
        'utm_source=coordinator_invite&utm_medium=invite_link&utm_campaign=invite_hub_invite_tools';
    return `${baseUrl}?${utmParams}`;
}

export function getInviteHubEnrollmentUrlQR(enrollmentCode: string) {
    const baseUrl = `${WEB_ENROLLMENT_ORIGIN}/enroll/${enrollmentCode}`;
    const utmParams =
        'utm_source=coordinator_invite&utm_medium=qr_code&utm_campaign=invite_hub_invite_tools';
    return `${baseUrl}?${utmParams}`;
}

export function getEnrollmentUrlWithAppsFlyerInviteTools(
    enrollmentCode: string,
    isAppsFlyerInviteToolsEnabled: boolean,
    isQRCode = false
) {
    if (isAppsFlyerInviteToolsEnabled) {
        const pid = isQRCode ? 'qrcode' : 'invitelink';
        return buildAppsFlyerEnrollmentOneLinkUrl(enrollmentCode, { pid, c: 'dashboard' });
    }

    if (isQRCode) {
        return getEnrollmentUrlQR(enrollmentCode);
    }
    return getEnrollmentUrl(enrollmentCode);
}
